'use client'

import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Button,
  ButtonLink,
  Dropzone,
  IconButton,
  Picker,
  SegmentedControl,
  SettingRow,
  Switch,
} from '@kiao-voice/ui-kit'
import { X } from 'lucide-react'
import {
  AUDIO_BATCH_ALLOWED_EXTENSIONS,
  AUDIO_BATCH_MAX_ENHANCEMENT_HINTS,
  AUDIO_BATCH_MAX_FILES,
  AUDIO_BATCH_MAX_TOTAL_FILE_BYTES,
} from '@kiao-voice/api-contract/batch-transcription'
import {
  MINIMUM_NOTES_CREDITS,
  MINUTES_PER_NOTES_CREDIT,
} from '@kiao-voice/core/meeting-notes-document'
import { NOTES_TEMPLATE_SCOPES, type AudioBatchLanguage } from '@kiao-voice/core'
import { APP_ROUTES } from '@/lib/app-routes'
import { formatFileSize } from './file-validation'
import { UploadHintsSection } from './upload-hints-section'
import { UploadLinksField } from './upload-links-field'
import type { NotesTemplate, NotesTemplateScope } from '@/features/meeting-notes/types'
import type { SelectionPreflightWarning } from './use-upload-selection-preflight'
import type { HintTerms } from './use-hint-terms'
import type { UploadDictionaries } from './use-upload-dictionaries'

const ACCEPT_EXTENSIONS = AUDIO_BATCH_ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',')
const DICTIONARIES_HREF = APP_ROUTES.dictionaries
/** The picker value that means "just the transcript, no 整理". */
const NOTES_TEMPLATE_NONE = 'none'
/** Source order shared by every template list: mine, then the team's, then built-in. */
const TEMPLATE_SCOPE_ORDER: readonly NotesTemplateScope[] = NOTES_TEMPLATE_SCOPES

/** Where this submission's media comes from: files from this device, or links. */
export type UploadSourceMode = 'files' | 'links'

export interface UploadSettingsBodyProps {
  submitting: boolean
  sourceMode: UploadSourceMode
  setSourceMode: (mode: UploadSourceMode) => void
  linkText: string
  setLinkText: (text: string) => void
  /** Usable links outside the media-platform list (see `parseSourceLinks`). */
  offPlatformLinkCount: number
  /** How many items Start would create: queued files, or usable links. */
  itemCount: number
  /** The previous attempt was cancelled — say so above the queue. */
  uploadCancelled: boolean
  alertMessages: string[]
  selectionWarning?: SelectionPreflightWarning | null
  onSelectionAction?: (url: string) => void
  queue: File[]
  addFiles: (incoming: FileList | File[]) => void
  removeFile: (index: number) => void
  openPicker: () => void
  fileInputRef: React.RefObject<HTMLInputElement | null>
  filePickerButtonRef: React.RefObject<HTMLButtonElement | null>
  filesRemaining: number
  effectiveMaxFileBytes: number
  isDragging: boolean
  setIsDragging: (dragging: boolean) => void
  taigiEnabled: boolean
  language: AudioBatchLanguage
  setLanguage: (language: AudioBatchLanguage) => void
  visualContextEligible: boolean
  visualContext: boolean
  setVisualContext: (enabled: boolean) => void
  templates: NotesTemplate[]
  templatesFailed: boolean
  notesTemplateId: number | null
  setNotesTemplateId: (id: number | null) => void
  selectedTemplate: NotesTemplate | null
  reloadWorkspaceOptions: () => void
  teamContext: boolean
  shareToTeam: boolean
  setShareToTeam: (share: boolean) => void
  hintTerms: HintTerms
  dictionary: UploadDictionaries
  openSaveDialog: (term: string) => void
  saveNotice: string | null
}

/**
 * Everything the user sets before pressing Start: the file queue, the language,
 * the on-screen-text option, the 整理範本, team sharing, and the vocabulary row.
 *
 * Rendered instead of the progress panel — the modal swaps the two, so this
 * never has to reason about an upload in flight beyond disabling its controls.
 */
export function UploadSettingsBody({
  submitting,
  sourceMode,
  setSourceMode,
  linkText,
  setLinkText,
  offPlatformLinkCount,
  itemCount,
  uploadCancelled,
  alertMessages,
  selectionWarning,
  onSelectionAction,
  queue,
  addFiles,
  removeFile,
  openPicker,
  fileInputRef,
  filePickerButtonRef,
  filesRemaining,
  effectiveMaxFileBytes,
  isDragging,
  setIsDragging,
  taigiEnabled,
  language,
  setLanguage,
  visualContextEligible,
  visualContext,
  setVisualContext,
  templates,
  templatesFailed,
  notesTemplateId,
  setNotesTemplateId,
  selectedTemplate,
  reloadWorkspaceOptions,
  teamContext,
  shareToTeam,
  setShareToTeam,
  hintTerms,
  dictionary,
  openSaveDialog,
  saveNotice,
}: UploadSettingsBodyProps) {
  const { t } = useTranslation('web')
  const sourceLabel = (scope: NotesTemplateScope) =>
    scope === 'personal'
      ? t('transcriptions.notes.groupMine')
      : scope === 'organization'
        ? t('transcriptions.notes.groupTeam')
        : t('transcriptions.notes.groupBuiltin')
  // Options stay name-only: at 264px a source or default marker costs the name
  // it sits beside. The source is carried by the group heading instead, and by
  // the helper text once the list is closed. The heading is the bare source
  // name: a heading longer than the options under it reads as one more option,
  // and the list only ever holds the current workspace's templates anyway.
  const templateOptions = TEMPLATE_SCOPE_ORDER.flatMap((scope) =>
    templates
      .filter((template) => template.scope === scope)
      .map((template) => ({
        value: String(template.id),
        label: template.name,
        description: template.description || undefined,
        group: sourceLabel(scope),
      }))
  )
  const {
    dictionaries,
    loading: dictionariesLoading,
    listFailed: dictionaryListFailed,
    selectedIds: selectedDictionaryIds,
    handlePickerChange: handleDictionaryPickerChange,
  } = dictionary
  const { hintLimitReached } = hintTerms

  const warningPlan = selectionWarning?.kind === 'duration' ? selectionWarning.plan : null
  const warningPlanName =
    warningPlan === 'creator'
      ? t('pricing.creator')
      : warningPlan === 'pro'
        ? t('pricing.pro')
        : warningPlan === 'team'
          ? t('transcriptions.team.team')
          : t('pricing.free')
  return (
    <div className="flex flex-col gap-4">
      {uploadCancelled ? (
        <p className="upnote">{t('transcriptions.progress.upload.cancelled')}</p>
      ) : null}
      <SegmentedControl
        aria-label={t('transcriptions.upload.sourceModeLabel')}
        variant="compact"
        className="self-start"
        value={sourceMode}
        disabled={submitting}
        options={[
          { value: 'files', label: t('transcriptions.upload.sourceModeFiles') },
          { value: 'links', label: t('transcriptions.upload.sourceModeLinks') },
        ]}
        onValueChange={setSourceMode}
      />
      {sourceMode === 'links' ? (
        <UploadLinksField
          value={linkText}
          onChange={setLinkText}
          disabled={submitting}
          offPlatformCount={offPlatformLinkCount}
        />
      ) : (
        <Dropzone
          dragging={isDragging}
          compact={queue.length > 0 ? true : undefined}
          onClick={() => {
            if (queue.length < AUDIO_BATCH_MAX_FILES) openPicker()
          }}
          onDragOver={(event) => {
            event.preventDefault()
            if (queue.length < AUDIO_BATCH_MAX_FILES) setIsDragging(true)
          }}
          onDragEnter={(event) => {
            event.preventDefault()
            if (queue.length < AUDIO_BATCH_MAX_FILES) setIsDragging(true)
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(event) => {
            event.preventDefault()
            setIsDragging(false)
            if (queue.length < AUDIO_BATCH_MAX_FILES && event.dataTransfer?.files?.length) {
              addFiles(event.dataTransfer.files)
            }
          }}
        >
          {queue.length === 0 ? (
            <>
              <p className="dz-title">{t('batchTranscription.dropHere')}</p>
              <p className="dz-spec meta">
                {t('batchTranscription.limitsLine', {
                  count: AUDIO_BATCH_MAX_FILES,
                  size: formatFileSize(effectiveMaxFileBytes),
                  totalSize: formatFileSize(AUDIO_BATCH_MAX_TOTAL_FILE_BYTES),
                  extensions: AUDIO_BATCH_ALLOWED_EXTENSIONS.join(', '),
                })}
              </p>
              <Button
                ref={filePickerButtonRef}
                variant="secondary"
                size="sm"
                disabled={submitting}
                onClick={(event) => {
                  event.stopPropagation()
                  openPicker()
                }}
              >
                {t('batchTranscription.chooseFiles')}
              </Button>
            </>
          ) : (
            <Button
              ref={filePickerButtonRef}
              variant="secondary"
              size="sm"
              disabled={submitting || filesRemaining === 0}
              onClick={(event) => {
                event.stopPropagation()
                openPicker()
              }}
            >
              {filesRemaining > 0
                ? t('transcriptions.upload.addMoreFiles', { count: filesRemaining })
                : t('transcriptions.upload.maximumFilesReached', {
                    max: AUDIO_BATCH_MAX_FILES,
                  })}
            </Button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            disabled={submitting || filesRemaining === 0}
            accept={ACCEPT_EXTENSIONS}
            aria-label={t('batchTranscription.chooseFiles')}
            onChange={(event) => {
              if (event.target.files) addFiles(event.target.files)
              event.target.value = ''
            }}
          />
        </Dropzone>
      )}

      {alertMessages.length > 0 ? (
        <Alert data-testid="upload-errors">{alertMessages.join('；')}</Alert>
      ) : null}

      {sourceMode === 'files' && queue.length > 0 ? (
        <div className="queue show queue--compact" aria-label={t('batchTranscription.queuedFiles')}>
          {queue.map((file, index) => (
            <div key={`${file.name}-${index}`} className="q-row">
              <span className="q-name" title={file.name}>
                {file.name}
              </span>
              <span className="q-size">{formatFileSize(file.size)}</span>
              <IconButton
                variant="ghost"
                disabled={submitting}
                aria-label={t('batchTranscription.removeFileAria', { name: file.name })}
                onClick={() => removeFile(index)}
              >
                <X aria-hidden="true" className="size-4" />
              </IconButton>
            </div>
          ))}
        </div>
      ) : null}

      {selectionWarning ? (
        <Alert data-testid="upload-selection-warning">
          <span className="flex flex-col items-start gap-3">
            <span>
              {selectionWarning.kind === 'credits' ? (
                t('transcriptions.upload.creditsShortBody', {
                  minutes: Math.ceil(selectionWarning.estimate.totalDurationSecs / 60),
                  needed: Math.round(selectionWarning.estimate.creditsNeeded * 10) / 10,
                  balance: Math.round(selectionWarning.estimate.creditsAvailable * 10) / 10,
                })
              ) : (
                <>
                  <strong className="break-all">{selectionWarning.fileName}</strong>
                  <br />
                  {t('transcriptions.upload.planLimitDurationBody', {
                    plan: warningPlanName,
                    actual: Math.ceil(selectionWarning.durationSecs / 60),
                    minutes: selectionWarning.maxFileMinutes,
                  })}
                </>
              )}
            </span>
            {selectionWarning.kind === 'credits' ? (
              <Button
                variant="secondary"
                onClick={() => onSelectionAction?.('/app/account/billing')}
              >
                {t('transcriptions.upload.creditsShortCta')}
              </Button>
            ) : warningPlan === 'free' || warningPlan === 'creator' ? (
              <ButtonLink
                variant="secondary"
                href="/pricing"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('transcriptions.upload.viewProPlan')}
              </ButtonLink>
            ) : null}
          </span>
        </Alert>
      ) : null}

      <div className="border-t border-[var(--border)]">
        {taigiEnabled ? (
          <SettingRow
            label={t('transcriptions.upload.languageTitle')}
            control={
              <div className="w-full md:w-[264px]">
                <Picker
                  label={t('transcriptions.upload.languageLabel')}
                  value={language}
                  disabled={submitting}
                  onChange={(value) => setLanguage(value === 'taigi' ? 'taigi' : 'auto')}
                  options={[
                    {
                      value: 'auto',
                      label: t('transcriptions.upload.languageAutoOption'),
                    },
                    {
                      value: 'taigi',
                      label: t('transcriptions.upload.languageTaigiOption'),
                    },
                  ]}
                />
              </div>
            }
          />
        ) : null}

        {visualContextEligible ? (
          <SettingRow
            label={t('transcriptions.upload.visualContextLabel')}
            control={
              <Switch
                checked={visualContext}
                disabled={submitting}
                aria-label={t('transcriptions.upload.visualContextLabel')}
                onChange={setVisualContext}
              />
            }
            supportingContent={
              <p className="mt-2 text-xs leading-5 text-[var(--muted)]">
                {visualContext
                  ? t('transcriptions.upload.visualContextEnabledDescription')
                  : t('transcriptions.upload.visualContextDisabledDescription')}
              </p>
            }
          />
        ) : null}

        <SettingRow
          label={t('batchTranscription.dictionaryTitle')}
          control={
            <Picker
              multiple
              className="w-full md:w-[264px]"
              label={t('batchTranscription.dictionaryTitle')}
              placeholder={t('batchTranscription.dictionaryPickerPlaceholder')}
              disabled={submitting || dictionariesLoading}
              value={selectedDictionaryIds.map(String)}
              onChange={handleDictionaryPickerChange}
              options={dictionaries.map((dictionary) => {
                const unavailableForSize = dictionary.entryCount > AUDIO_BATCH_MAX_ENHANCEMENT_HINTS
                const unavailableForCapacity =
                  hintLimitReached && !selectedDictionaryIds.includes(dictionary.id)
                const disabledReason = unavailableForSize
                  ? t('batchTranscription.dictionaryTooManyTerms', {
                      max: AUDIO_BATCH_MAX_ENHANCEMENT_HINTS,
                    })
                  : unavailableForCapacity
                    ? t('batchTranscription.dictionaryHintLimitReached', {
                        max: AUDIO_BATCH_MAX_ENHANCEMENT_HINTS,
                      })
                    : undefined
                return {
                  value: String(dictionary.id),
                  label: dictionary.name,
                  description: t('batchTranscription.dictionaryEntryCount', {
                    count: dictionary.entryCount,
                  }),
                  disabled: Boolean(disabledReason),
                  disabledReason,
                }
              })}
              emptyLabel={t('batchTranscription.dictionaryPickerEmpty')}
              emptyContent={
                dictionaryListFailed ? (
                  <div className="px-2 py-3 text-xs leading-5 text-[var(--error)]">
                    <p data-testid="dictionary-load-error">
                      {t('batchTranscription.dictionaryLoadFailed')}
                    </p>
                    <button
                      type="button"
                      className="underline underline-offset-4"
                      onClick={() => void reloadWorkspaceOptions()}
                    >
                      {t('batchTranscription.dictionaryRetryList')}
                    </button>
                  </div>
                ) : !dictionariesLoading && dictionaries.length === 0 ? (
                  <div className="px-2 py-3 text-xs leading-5 text-[var(--muted)]">
                    <p>{t('batchTranscription.dictionaryEmpty')}</p>
                    <a
                      className="underline underline-offset-4 hover:text-[var(--fg)]"
                      href={DICTIONARIES_HREF}
                      target="_blank"
                      rel="noopener"
                    >
                      {t('batchTranscription.dictionaryCreateLink')}
                    </a>
                  </div>
                ) : undefined
              }
              footer={
                <a
                  className="block text-xs text-[var(--muted)] underline underline-offset-4 hover:text-[var(--fg)]"
                  href={DICTIONARIES_HREF}
                  target="_blank"
                  rel="noopener"
                >
                  {t('batchTranscription.dictionaryManageLink')} ↗
                </a>
              }
            />
          }
          supportingContent={
            <>
              {dictionaryListFailed ? (
                <p className="mt-2 text-xs leading-5 text-[var(--error)]" role="status">
                  {t('batchTranscription.dictionaryLoadFailed')}{' '}
                  <button
                    type="button"
                    className="underline underline-offset-4"
                    onClick={() => void reloadWorkspaceOptions()}
                  >
                    {t('batchTranscription.dictionaryRetryList')}
                  </button>
                </p>
              ) : null}
              <p className="mt-2 text-xs leading-5 text-[var(--muted)]">
                {t('batchTranscription.dictionaryDescription')}
              </p>
            </>
          }
        />

        <UploadHintsSection
          hintTerms={hintTerms}
          dictionary={dictionary}
          submitting={submitting}
          openSaveDialog={openSaveDialog}
          saveNotice={saveNotice}
        />

        <SettingRow
          label={t('transcriptions.upload.afterHeading')}
          control={
            <Picker
              className="w-full md:w-[264px]"
              label={t('transcriptions.upload.afterPickerLabel')}
              searchPlaceholder={t('transcriptions.upload.afterSearchPlaceholder')}
              disabled={submitting}
              value={notesTemplateId === null ? NOTES_TEMPLATE_NONE : String(notesTemplateId)}
              onChange={(value) =>
                setNotesTemplateId(value === NOTES_TEMPLATE_NONE ? null : Number(value))
              }
              options={[
                { value: NOTES_TEMPLATE_NONE, label: t('transcriptions.upload.afterNone') },
                ...templateOptions,
              ]}
            />
          }
          supportingContent={
            templatesFailed || selectedTemplate ? (
              <>
                {templatesFailed ? (
                  <p className="mt-2 text-xs leading-5 text-[var(--muted)]">
                    {t('transcriptions.upload.afterTemplatesFailed')}
                  </p>
                ) : null}
                {selectedTemplate ? (
                  <p className="mt-2 text-xs leading-5 text-[var(--muted)]">
                    {selectedTemplate.description
                      ? t('transcriptions.upload.afterProducesFrom', {
                          source: sourceLabel(selectedTemplate.scope),
                          description: selectedTemplate.description,
                        })
                      : sourceLabel(selectedTemplate.scope)}{' '}
                    {t('transcriptions.upload.afterCreditNotice', {
                      count: MINIMUM_NOTES_CREDITS,
                      minutes: MINUTES_PER_NOTES_CREDIT,
                    })}
                  </p>
                ) : null}
                {selectedTemplate && itemCount > 1 ? (
                  <p className="mt-2 text-xs leading-5 text-[var(--muted)]">
                    {t('transcriptions.upload.afterBatchNote', {
                      count: itemCount,
                      name: selectedTemplate.name,
                    })}
                  </p>
                ) : null}
              </>
            ) : null
          }
        />

        {teamContext ? (
          <SettingRow
            label={t('transcriptions.upload.shareToTeam')}
            control={
              <Switch
                checked={shareToTeam}
                disabled={submitting}
                aria-label={t('transcriptions.upload.shareToTeam')}
                onChange={setShareToTeam}
              />
            }
            supportingContent={
              shareToTeam ? (
                <p className="mt-2 text-xs leading-5 text-[var(--muted)]">
                  {t('transcriptions.upload.shareToTeamDescription')}
                </p>
              ) : null
            }
          />
        ) : null}
      </div>
    </div>
  )
}
